import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, BarChart2, Users, FileText, Wrench, Briefcase, ChevronLeft, Download, User as UserIcon, Building2, Bell, Shield, LayoutGrid, LogOut, LifeBuoy, Users2, Package as PackageIcon, Zap, Banknote, Calendar as CalendarIcon } from 'lucide-react';
import { Role, ReportType } from '../../types';
import { useAppContext } from '../../hooks/useAppContext';
import useAppStore from '../../store/useAppStore';
import toast from 'react-hot-toast';

const employeeNavColors: { [key: string]: string } = {
    sales: 'from-blue-500 to-blue-600',
    maintenance: 'from-emerald-500 to-emerald-600',
    project: 'from-orange-500 to-orange-600',
    log: 'from-cyan-500 to-cyan-600',
    workflow: 'from-indigo-500 to-indigo-600',
    profile: 'from-rose-500 to-rose-600',
    analytics: 'from-violet-500 to-violet-600',
    support: 'from-sky-500 to-sky-600',
    'team-projects': 'from-orange-500 to-orange-600',
    'completed-projects': 'from-orange-500 to-orange-600',
};

export const ModernSidebar: React.FC = () => {
    const { t, user, logout } = useAppContext();
    const {
        isSidebarCollapsed: isCollapsed,
        toggleSidebar,
        isMobileMenuOpen,
        setMobileMenuOpen
    } = useAppStore();
    const { currentUserLedTeam } = useAppStore();
    const location = useLocation();

    if (!user) return null;

    const handleSignOut = () => {
        try {
            logout();
        } catch (error: any) {
            toast.error(`فشل تسجيل الخروج: ${error.message}`);
        }
    };

    const baseNav = [
        { id: 'dashboard', path: '/', label: t('dashboard'), icon: Home },
    ];

    const employeeNav = [
        ...baseNav,
        { id: 'sales', path: '/sales', label: t('salesReports'), icon: FileText },
        { id: 'maintenance', path: '/maintenance', label: t('maintenanceReports'), icon: Wrench },
        { id: 'project', path: '/projects', label: t('projectReports'), icon: Briefcase },
        { id: 'calendar', path: '/calendar', label: t('calendar'), icon: CalendarIcon },
        { id: 'log', path: '/log', label: t('reportsLog'), icon: BarChart2 },
        { id: 'workflow', path: '/workflow', label: t('importExport'), icon: Download },
        { id: 'packages', path: '/packages', label: t('packageManagement'), icon: PackageIcon },

        { id: 'profile', path: '/profile', label: t('profile'), icon: UserIcon },
        { id: 'support', path: '/support', label: t('techSupport'), icon: LifeBuoy },
    ];

    const adminNav = [
        ...baseNav,
        { id: 'workflow', path: '/workflow', label: t('importExport'), icon: Download },
        { id: 'calendar', path: '/calendar', label: t('calendar'), icon: CalendarIcon },
        { id: 'manageEmployees', path: '/employees', label: t('manageEmployees'), icon: Users },
        { id: 'manageBranches', path: '/branches', label: t('manageBranches'), icon: Building2 },
        { id: 'manageTeams', path: '/teams', label: t('manageTeams'), icon: Users2 },
        { id: 'permissions', path: '/permissions', label: t('managePermissions'), icon: Shield },
        { id: 'showcase', path: '/showcase', label: t('adminCenter'), icon: LayoutGrid },
        { id: 'admin-packages', path: '/admin-packages', label: t('packageManagement'), icon: PackageIcon },
        { id: 'support', path: '/support', label: t('techSupport'), icon: LifeBuoy },
        { id: 'profile', path: '/profile', label: t('profile'), icon: UserIcon },
    ];

    let finalEmployeeNav = employeeNav;

    if (user.role === Role.TeamLead || user.role === Role.Developer || !!currentUserLedTeam) {
        finalEmployeeNav = [
            ...baseNav,
            { id: 'team-projects', path: '/team-projects', label: t('teamProjects'), icon: Users2 },
            { id: 'calendar', path: '/calendar', label: t('calendar'), icon: CalendarIcon },
            { id: 'log', path: '/log', label: t('reportsLog'), icon: BarChart2 },
        ];
        if (!!user.hasPackageManagementPermission) {
            finalEmployeeNav.push({ id: 'packages', path: '/packages', label: t('packageManagement'), icon: PackageIcon });
        }
        if (!!user.hasImportExportPermission) {
            finalEmployeeNav.push({ id: 'workflow', path: '/workflow', label: t('importExport'), icon: Download });
        }
        finalEmployeeNav.push({ id: 'profile', path: '/profile', label: t('profile'), icon: UserIcon });

    } else if (user.role !== Role.Admin) {
        const allowed = user.allowedReportTypes || [];
        finalEmployeeNav = employeeNav.filter(item => {
            if (item.id === 'workflow') return !!user.hasImportExportPermission;
            if (item.id === 'packages') return !!user.hasPackageManagementPermission;
            if (user.hasImportExportPermission && ['sales', 'maintenance', 'project'].includes(item.id)) return false;
            if (user.hasPackageManagementPermission && ['sales', 'maintenance', 'project'].includes(item.id)) return false;
            if (['sales', 'maintenance', 'project'].includes(item.id)) {
                const map: Record<string, ReportType> = {
                    sales: ReportType.Sales,
                    maintenance: ReportType.Maintenance,
                    project: ReportType.Project,
                };
                return allowed.includes(map[item.id]);
            }
            return true;
        });
    }

    const navItems = user.role === Role.Admin ? adminNav : finalEmployeeNav;

    return (
        <aside className={`fixed top-4 bottom-4 right-4 z-[60] glass-panel rounded-2xl flex flex-col transition-all duration-500 ease-out
            ${isCollapsed ? 'w-20' : 'w-72'}
            ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-[120%] lg:translate-x-0'}
        `}>
            {/* Logo Section */}
            <div className={`flex items-center justify-center h-24 mb-2 transition-all duration-500 ${isCollapsed ? 'opacity-0 scale-0 h-0 overflow-hidden' : 'opacity-100 scale-100'}`}>
                <div className="relative group cursor-pointer">
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
                    <img
                        src="https://www2.0zz0.com/2025/09/11/07/879817109.png"
                        alt="Qssun Company Logo"
                        className="relative h-14 w-auto drop-shadow-xl transform transition-transform group-hover:scale-105"
                    />
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 flex flex-col gap-2 overflow-y-auto px-3 py-2 scrollbar-hide">
                {navItems.map(item => {
                    return (
                        <NavLink
                            key={item.id}
                            to={item.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={({ isActive }) => `
                                relative flex items-center px-4 py-3.5 rounded-xl transition-all duration-300 group overflow-hidden
                                ${isActive ? 'text-white shadow-lg shadow-black/10' : 'text-slate-400 hover:text-white hover:bg-white/5'}
                                ${isCollapsed ? 'justify-center px-0' : ''}
                            `}
                        >
                            {({ isActive }) => {
                                const gradientClass = isActive
                                    ? (user.role !== Role.Admin && item.id !== 'dashboard' ? employeeNavColors[item.id] || 'from-blue-500 to-blue-600' : 'from-blue-500 to-blue-600')
                                    : '';

                                return (
                                    <>
                                        {isActive && (
                                            <div className={`absolute inset-0 bg-gradient-to-r ${gradientClass} opacity-90 backdrop-blur-sm`}></div>
                                        )}

                                        <div className="relative z-10 flex items-center gap-4">
                                            <item.icon
                                                size={isCollapsed ? 24 : 20}
                                                className={`transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`}
                                                strokeWidth={isActive ? 2.5 : 2}
                                            />
                                            {!isCollapsed && (
                                                <span className="font-medium tracking-wide text-sm">{item.label}</span>
                                            )}
                                        </div>

                                        {/* Active Indicator Dot */}
                                        {isActive && !isCollapsed && (
                                            <div className="absolute left-3 w-1.5 h-1.5 rounded-full bg-white shadow-glow animate-pulse"></div>
                                        )}
                                    </>
                                );
                            }}
                        </NavLink>
                    );
                })}
            </nav>

            {/* Footer Actions */}
            <div className="p-4 mt-auto space-y-3">
                {!isCollapsed && (
                    <div className="w-full flex justify-center py-2 opacity-50 hover:opacity-100 transition-opacity">
                        <img
                            src="https://www2.0zz0.com/2025/10/21/12/215289676.gif"
                            alt="Developer Logo"
                            className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-500"
                        />
                    </div>
                )}

                <button
                    className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-between px-4'} py-3 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-all border border-red-500/10 hover:border-red-500/30 group`}
                    onClick={handleSignOut}
                    title={t('signOut')}
                >
                    <div className="flex items-center gap-3">
                        <LogOut size={20} className="group-hover:-translate-x-1 transition-transform" />
                        {!isCollapsed && <span className="font-medium text-sm">{t('logout')}</span>}
                    </div>
                </button>
            </div>

            {/* Collapse Toggle */}
            <button
                className="absolute top-6 -left-3 w-6 h-6 bg-slate-800 border border-slate-700 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-blue-600 hover:border-blue-500 transition-all shadow-lg z-50"
                onClick={toggleSidebar}
            >
                <ChevronLeft size={14} className={`${isCollapsed ? 'rotate-180' : ''} transition-transform duration-300`} />
            </button>
        </aside>
    );
};
